import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import {
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Info from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CopyrightIcon from '@mui/icons-material/Copyright';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Tooltip, Fade, CircularProgress, Backdrop } from '@mui/material';
import { useLocation, Link } from 'react-router-dom';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

function IncomeExpenseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [mounted, setMounted] = useState(false);

    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Accounts');
    const hasEditMasterPermission = permissions.includes('Edit Accounts');
    const hasDeleteMasterPermission = permissions.includes('Delete Accounts');
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setMounted(true);
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        if (location.state?.from === '/IncomeExpenseCreate' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else if (location.state?.from === '/IncomeExpenseEdit' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else if (location.state?.from === '/IncomeExpenseDetails' && shouldReload) {
            localStorage.removeItem('shouldReload');
            window.location.reload();
        }
        else {
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
        }
    }, [location]);

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage');
            window.location.reload();
        }

        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const fetchTableData = async () => {
        setLoading(true);
        setData([]);
        try {
            const response = await fetchData('IncomeExpenseApi', `/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching SaleOrder Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        } finally {
            setLoading(false);
        }

    };

    const handleDelete = async () => {
        deleteData('IncomeExpenseApi', `${deleteItemId}`, token)
            .then(() => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('IncomeExpenseApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('PurchaseOrderDate');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sortedData = stableSort(filteredData, getComparator(order, orderBy));

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .action-button {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .table-row {
                        transition: all 0.2s ease;
                    }
                    .table-row:hover {
                        background: #f8f9fa !important;
                        transform: scale(1.01);
                    }
                `}</style>

                {loading && (
                    <Backdrop
                        open={loading}
                        sx={{
                            color: '#fff',
                            zIndex: 9999,
                            backdropFilter: 'blur(4px)',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px'
                        }}>
                            <CircularProgress
                                size={60}
                                thickness={4}
                                sx={{
                                    color: '#667eea',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }}
                            />
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'white',
                                letterSpacing: '0.5px',
                                textAlign: 'center'
                            }}>
                                Loading Income & Expense Data...
                            </div>
                        </div>
                    </Backdrop>
                )}

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative'
                }}>
                    {/* Header Section */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '8px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.3)'
                            }}>
                                <AccountBalanceWalletIcon style={{ fontSize: '22px', color: 'white' }} />
                            </div>
                            <div>
                                <h1 style={{
                                    fontSize: '20px',
                                    fontWeight: '700',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    margin: 0,
                                    letterSpacing: '-0.3px'
                                }}>
                                    Income & Expense
                                </h1>
                                <p style={{ margin: 0, color: '#666', fontSize: '11px' }}>
                                    Manage income and expense records
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={handleSearch}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                style={{
                                    width: isFocused ? '200px' : '150px',
                                    height: '32px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    padding: '0 12px',
                                    transition: 'all 0.3s ease',
                                    outline: 'none',
                                    borderColor: isFocused ? '#667eea' : '#e0e0e0',
                                    boxShadow: isFocused ? '0 2px 8px rgba(102, 126, 234, 0.2)' : 'none',
                                    fontSize: '13px'
                                }}
                            />

                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <div>
                                    <label style={{ fontSize: '10px', color: '#666', display: 'block', marginBottom: '2px' }}>
                                        From
                                    </label>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={handleFromDateChange}
                                        style={{
                                            height: '32px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            padding: '0 8px',
                                            fontSize: '12px',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: '10px', color: '#666', display: 'block', marginBottom: '2px' }}>
                                        To
                                    </label>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={handleToDateChange}
                                        style={{
                                            height: '32px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '8px',
                                            padding: '0 8px',
                                            fontSize: '12px',
                                            outline: 'none'
                                        }}
                                    />
                                </div>
                            </div>

                            <Tooltip title="Refresh data" arrow>
                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    startIcon={<RefreshIcon />}
                                    className="action-button"
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                        color: 'white',
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)',
                                        border: 'none',
                                        minHeight: '32px'
                                    }}
                                >
                                    Refresh
                                </Button>
                            </Tooltip>

                            {hasCreateMasterPermission && (
                                <Tooltip title="Create new income or expense" arrow>
                                    <Button
                                        variant="contained"
                                        component={Link}
                                        to="/IncomeExpenseCreate"
                                        startIcon={<AddIcon />}
                                        className="action-button"
                                        style={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '6px 16px',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            border: 'none',
                                            minHeight: '32px'
                                        }}
                                    >
                                        Create New
                                    </Button>
                                </Tooltip>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="income expense table">
                                <TableHead>
                                    <TableRow style={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                    }}>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Sl.No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Bill Date
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Bill No
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Name
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Type
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Amount
                                        </TableCell>
                                        <TableCell style={{
                                            color: 'white',
                                            fontWeight: '600',
                                            fontSize: '11px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            padding: '8px 12px'
                                        }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                                        <TableRow
                                            key={item.Id}
                                            className="table-row"
                                            style={{
                                                background: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                                            }}
                                        >
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {formatDate(item.BillDate)}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {item.BillNo}/{item.FinYear}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748'
                                            }}>
                                                {item.MasterName}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: item.IsTypeDescription === 'Debit' ? '#e53e3e' : '#38a169',
                                                fontWeight: '600'
                                            }}>
                                                {item.IstypeName}
                                            </TableCell>
                                            <TableCell style={{
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                color: '#2d3748',
                                                fontWeight: '600'
                                            }}>
                                                {item.TotalAmount ? item.TotalAmount.toFixed(2) : "0.00"}
                                            </TableCell>
                                            <TableCell style={{ padding: '6px 12px' }}>
                                                <Tooltip title="View Details" arrow>
                                                    <IconButton size="small" component={Link} to={`/IncomeExpenseDetails/${item.Id}`}>
                                                        <Info style={{ color: 'darksalmon', fontSize: '19px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                {hasEditMasterPermission && (
                                                    <Tooltip title="Edit" arrow>
                                                        <IconButton size="small" component={Link} to={`/IncomeExpenseEdit/${item.Id}`}>
                                                            <EditIcon style={{ fontSize: '19px', color: '#667eea' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {hasDeleteMasterPermission && (
                                                    <Tooltip title={item.IsActive ? "Delete" : "Activate"} arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
                                                            color="error"
                                                        >
                                                            {item.IsActive ? (
                                                                <DeleteIcon style={{ fontSize: '19px' }} />
                                                            ) : (
                                                                <ActivateIcon style={{ color: 'darkolivegreen', fontSize: '19px' }} />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableContainer>
                    </div>
                </div>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={confirmDialogOpen}
                    onClose={closeConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{
                        style: {
                            borderRadius: '12px',
                            padding: '8px'
                        }
                    }}
                >
                    <DialogTitle id="alert-dialog-title" style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#2d3748'
                    }}>
                        Delete Record?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description" style={{
                            fontSize: '13px',
                            color: '#666'
                        }}>
                            Are you sure you want to delete this record?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 16px' }}>
                        <Button
                            onClick={closeConfirmDialog}
                            style={{
                                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                                color: '#ffffff',
                                borderRadius: '8px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                textTransform: 'none'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            style={{
                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                color: '#ffffff',
                                borderRadius: '8px',
                                padding: '6px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                textTransform: 'none'
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Footer */}
                <div style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                    fontFamily: 'serif',
                    fontWeight: '600',
                    color: '#2c3e50',
                    fontSize: '13px',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    zIndex: 1000
                }}>
                    <CopyrightIcon style={{ fontSize: '18px', color: '#667eea' }} />
                    <span>EFFTABS SOLUTIONS LLP</span>
                </div>
            </div>
        </Fade>
    );
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export default IncomeExpenseIndex;

